import { ShoppingBag, Store, Instagram, ExternalLink, Info, Search } from "lucide-react";
import { ROUTES, SITE } from "../site";
import { usePageMeta } from "../hooks/usePageMeta";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import WavyDivider from "../components/WavyDivider";

const meta = ROUTES[3];

const ONLINE = [
  {
    testid: "beli-online-yupiland",
    icon: ShoppingBag,
    color: "bg-bun-soft text-bun-dark",
    name: "Yupiland Official Store",
    text: "Toko online resmi Yupi — tempat paling pas untuk membeli Yupi Burger dan varian gummy lainnya secara online.",
    href: SITE.official.store,
    cta: "Kunjungi Toko",
  },
  {
    testid: "beli-online-yupi-site",
    icon: Store,
    color: "bg-lettuce-soft text-lettuce-dark",
    name: "Situs Resmi Yupi",
    text: "Informasi produk, berita, dan channel resmi lainnya dari PT Yupi Indo Jelly Gum Tbk.",
    href: SITE.official.corporate,
    cta: "Buka yupi.co.id",
  },
  {
    testid: "beli-online-instagram",
    icon: Instagram,
    color: "bg-berry-soft text-berry-dark",
    name: "Instagram @yupi.gummy",
    text: "Ikuti promo, konten seru, dan info produk terbaru langsung dari akun resmi Yupi.",
    href: SITE.official.instagram,
    cta: "Follow Instagram",
  },
];

const OFFLINE = [
  { name: "Minimarket", text: "Alfamart, Indomaret, dan jaringan minimarket lain di kotamu." },
  { name: "Toko Kelontong & Warung", text: "Warung dan toko kelontong terdekat — Yupi Burger memang camilan khas warung." },
  { name: "Supermarket", text: "Rak permen dan camilan di supermarket besar di seluruh Indonesia." },
];

export default function Beli() {
  usePageMeta(meta.title, meta.description);

  return (
    <div data-testid="beli-page">
      <Header />
      <main>
        <Breadcrumbs current="Dimana Beli" />
        <section className="container-site pb-12 pt-8 sm:pb-16">
          <span className="eyebrow-chip">Dimana Beli</span>
          <h1 className="mt-4 max-w-3xl text-balance font-display text-4xl font-extrabold leading-tight tracking-tight text-plum sm:text-5xl">
            Gampang Banget Nemuin Yupi Burger
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-plum-soft sm:text-lg">
            Dari warung dekat rumah sampai toko online resmi — ini tempat-tempat yang bisa kamu
            datangi untuk membeli Yupi Burger.
          </p>
        </section>

        <section aria-labelledby="online-heading" className="container-site pb-14 sm:pb-20">
          <h2 id="online-heading" className="font-display text-2xl font-bold tracking-tight text-plum sm:text-3xl">
            Belanja Online di Channel Resmi
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
            {ONLINE.map((channel) => (
              <a
                key={channel.testid}
                href={channel.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={channel.testid}
                className="card-gummy group flex flex-col"
              >
                <span className={`inline-flex w-fit rounded-2xl p-3.5 ${channel.color}`}>
                  <channel.icon className="h-7 w-7" aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-display text-lg font-bold text-plum">{channel.name}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-plum-soft">{channel.text}</p>
                <span className="mt-5 inline-flex items-center gap-2 font-display text-sm font-bold text-patty transition-colors group-hover:text-patty-dark">
                  {channel.cta}
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                </span>
              </a>
            ))}
          </div>
          <div
            data-testid="beli-marketplace-tip"
            className="mt-6 flex items-start gap-3 rounded-3xl border-2 border-plum/10 bg-white p-5 text-sm leading-relaxed text-plum-soft"
          >
            <Search className="mt-0.5 h-5 w-5 shrink-0 text-bun-dark" aria-hidden="true" />
            <p>
              Belanja di marketplace? Cari <strong className="text-plum">“Yupi Official Store”</strong>{" "}
              di Tokopedia, Shopee, atau platform favoritmu untuk memastikan produk yang kamu beli
              berasal dari toko resmi.
            </p>
          </div>
        </section>

        <section aria-labelledby="offline-heading" className="bg-patty-soft">
          <WavyDivider fill="#FFFDF9" flip />
          <div className="container-site py-14 sm:py-20">
            <h2 id="offline-heading" className="font-display text-2xl font-bold tracking-tight text-plum sm:text-3xl">
              Beli Langsung di Toko Terdekat
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
              {OFFLINE.map((place) => (
                <div
                  key={place.name}
                  data-testid={`beli-offline-${place.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-3xl border-2 border-plum/10 bg-white p-6 shadow-gummy sm:p-7"
                >
                  <span className="inline-flex rounded-2xl bg-patty-soft p-3 text-patty-dark">
                    <Store className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold text-plum">{place.name}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-plum-soft">{place.text}</p>
                </div>
              ))}
            </div>
            <div
              data-testid="beli-availability-note"
              className="mx-auto mt-8 flex max-w-3xl items-start gap-3 rounded-3xl border-2 border-cheese-dark/30 bg-cheese-soft p-5 text-sm leading-relaxed text-plum"
            >
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-bun-dark" aria-hidden="true" />
              <p>
                Ketersediaan varian dan harga dapat berbeda di setiap toko. Kalau varian yang kamu
                cari sedang kosong, coba cek toko lain atau pesan lewat Yupiland Official Store.
              </p>
            </div>
          </div>
          <WavyDivider fill="#FFFDF9" />
        </section>

        <section aria-label="CTA toko resmi" className="container-site py-14 text-center sm:py-20">
          <h2 className="mx-auto max-w-2xl text-balance font-display text-2xl font-bold tracking-tight text-plum sm:text-3xl">
            Cara Paling Gampang: Langsung ke Toko Resminya
          </h2>
          <a
            href={SITE.official.store}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="beli-cta-yupiland-main"
            className="btn-primary mt-8 animate-bounce-slow !px-9 !py-4 !text-lg"
          >
            <ShoppingBag className="h-5 w-5" aria-hidden="true" />
            Belanja di Yupiland Official Store
          </a>
        </section>
      </main>
      <Footer />
    </div>
  );
}
